import { FC, useRef, useEffect, useState } from 'react';
import { IonItem, IonLabel, IonText, IonButton } from '@ionic/react';
import { IRecording } from './RecordingState';
import { CloudExclamationIcon, UploadIcon, CheckIcon, PlayIcon, PauseIcon, LockIcon, WaveformLinesIcon } from '../../theme/Icons';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { RecordingState } from './RecordingState';
import { useSubscription } from '@meraki-internal/state';
import { CaremapNotesModelFactory } from '../../notes/model/CaremapNotesModelFactory';
import { RecordingRetryButton } from './RecordingRetryButton';
import dayjs from 'dayjs';

const formatRecordingDuration = (msDuration: number): string => {
    if (!msDuration) return '0s';

    const totalSeconds = Math.floor(msDuration / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes < 1 ? '' : minutes + 'm '}${seconds}s`;
};

const formatTime = (seconds: number): string => {
    if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) return '0:00';

    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const formatNoteDate = (date: string): string => {
    const dayjsDate = dayjs(date);
    if (dayjsDate.isToday()) {
        return dayjsDate.format('h:mma');
    }
    return dayjsDate.format('MM/DD/YY');
};

export const OfflineRecordingListItem: FC<{ recording: IRecording; showBottomLine?: boolean }> = ({ recording, showBottomLine = false }) => {
    const recordingState = useInstance(RecordingState);

    const notesFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesFactory);

    const audioRef = useRef<HTMLAudioElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [, setDuration] = useState(0);
    const [progress, setProgress] = useState(0);

    // Check if the note is private by finding it via noteId
    const isPrivate = (() => {
        try {
            const note = recordingState.findNote(recording.noteId);
            return note?.isPrivate ?? false;
        } catch {
            return false;
        }
    })();

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handleLoadedMetadata = () => {
            setDuration(audio.duration || 0);
        };

        const handleTimeUpdate = () => {
            const current = audio.currentTime || 0;
            setCurrentTime(current);
            const dur = audio.duration || 0;
            setProgress(dur > 0 ? current / dur : 0);
        };

        const handleEnded = () => {
            setIsPlaying(false);
            setCurrentTime(0);
            setProgress(0);
        };

        const handleCanPlay = () => {
            if (audio.duration && isFinite(audio.duration)) {
                setDuration(audio.duration);
            }
        };

        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('canplay', handleCanPlay);

        return () => {
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('canplay', handleCanPlay);
        };
    }, []);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        // Convert ArrayBuffer to File for audio playback
        const file = new File([recording.file], recording.fileName, { type: recording.mimeType });
        const url = URL.createObjectURL(file);
        audio.src = url;

        return () => {
            URL.revokeObjectURL(url);
        };
    }, [recording.file, recording.fileName, recording.mimeType]);

    const togglePlayback = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isPlaying) {
            audio.pause();
        } else {
            audio.play().catch(console.error);

        }
        setIsPlaying(!isPlaying);
    };


    const getStatusIcon = () => {
        if (recording.status === 'uploaded') {
            return <CheckIcon color={'success' as const} />;
        } else if (recording.status === 'uploading') {
            return <UploadIcon color={'primary' as const} />;
        } else {
            return <CloudExclamationIcon color={'secondary' as const} />;
        }
    };

    const getStatusMessage = () => {
        if (recording.status === 'uploaded') {
            return 'Synced successfully';
        } else if (recording.status === 'uploading') {
            return 'Syncing...';
        } else {
            return 'Will sync when online';
        }
    };

    return (
        <IonItem
            button={false}
            detail={false}
            lines={showBottomLine ? 'inset' : 'none'}
            data-type="offline-recording-item"
            data-id={`offline-recording-${recording.id}`}
            data-recording-id={recording.id}
            style={{'--border-radius': '8px'}}
        >
            <IonLabel class="ion-text-wrap">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <IonText color="dark">
                        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 500 }}>
                            Audio Note
                        </h3>
                    </IonText>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        {isPrivate && <LockIcon color="secondary" size="small" />}
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', color: 'var(--ion-color-step-600, #666666)', fontSize: '15px', fontWeight: 500, marginBottom: '8px' }}>
                    {formatNoteDate(recording.created)}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '14px', color: 'var(--ion-color-step-600, #666666)' }}>
                    <WaveformLinesIcon color="medium" />
                    <span>{formatRecordingDuration(recording.msDuration)}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }} data-id={`recording-status-${recording.id}`}>
                        {getStatusIcon()}
                        (Offline) {getStatusMessage()}
                    </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <IonButton
                        fill="clear"
                        size="small"
                        onClick={togglePlayback}
                        data-id={`play-offline-recording-${recording.id}`}
                    >
                        {isPlaying ? <PauseIcon size="small" /> : <PlayIcon size="small" />}
                    </IonButton>

                    <div style={{
                        flex: 1,
                        height: '6px',
                        backgroundColor: 'var(--ion-color-light)',
                        borderRadius: '3px',
                        overflow: 'hidden'
                    }}>
                        <div
                            style={{
                                height: '100%',
                                width: `${Math.max(0, Math.min(100, progress * 100))}%`,
                                backgroundColor: 'var(--ion-color-primary)',
                                transition: 'width 0.1s ease-out'
                            }}
                        />
                    </div>

                    <IonText color="medium" style={{ fontSize: '12px', minWidth: '40px' }}>
                        {formatTime(currentTime)}
                    </IonText>

                    {(recording.status === 'upload-failed' || recording.status === 'offline-pending') && (
                        <RecordingRetryButton recordingId={recording.id} />
                    )}
                </div>

                <audio
                    ref={audioRef}
                    preload="metadata"
                    style={{ display: 'none' }}
                    data-id="audio-element"
                />
            </IonLabel>
        </IonItem>
    );
};
